import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";

import StyledLink from "components/Styled/Link";
import { fetcherAuth } from "utils/api";
import { REFERRAL_CREDIT } from "utils/influencer";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

type ReferralCredit = {
  id: number;
  createdAt: number;
  description: string;
  amount: number; // in cents
};

interface Props {
  hideBalance?: boolean;
  source?: string;
}

export default function Referral({
  hideBalance,
  source = "Settings Page",
}: Props) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { referralCode } = useContext(SubscriptionContext);

  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const [balance, setBalance] = useState<number>(0);
  const [referrals, setReferrals] = useState<ReferralCredit[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [viewReferrals, setViewReferrals] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchCustomerBalance = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/customer/balance`,
      );
      setBalance(res.balance);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTransactions = async (creditId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/referrals/credits?per_page=10`;
      if (creditId) {
        url += `&cursor=${creditId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setReferrals((prev) => [...prev, ...res.credits]);
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
    }

    trackEvent("Referral Link Copied to Clipboard", { source });

    if (setAlert) setAlert("Copied to clipboard!", "success");
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchCustomerBalance();
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const formatBalance = (balance: number) => {
    // Balance is in cents. Since balance is in negative if we owe the user
    // we need to reverse it for UI to show them as credit amount.
    return Number((balance / 100) * -1)?.toFixed(2);
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        <Box
          component="i"
          className="fa-regular fa-gift"
          sx={{ color: "#f0944a" }}
        />{" "}
        Want free credits on Bento?
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Refer a friend, earn credits! Give ${REFERRAL_CREDIT} to a new user. Get
        ${REFERRAL_CREDIT} when they upgrade.{" "}
        <StyledLink
          href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento%20Referral%20Program%20T%2BC's%20-%20Google%20Docs.pdf"
          onClick={() =>
            trackEvent("Referral Terms and Conditions Clicked", { source })
          }
        >
          T&Cs apply
        </StyledLink>
      </Typography>

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Your unique referral link
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 1,
            bgcolor: "grey.50",
            maxWidth: 520,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontFamily: "monospace",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.primary",
            }}
          >
            {referralCode}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleCopyToClipboard}
          disableElevation
          sx={{
            whiteSpace: "nowrap",
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "#f4f5f7",
            color: "text.primary",
            textTransform: "none",
            "&:hover": { bgcolor: "#e6e8eb" },
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-clipboard"
            sx={{ mr: 0.5 }}
          />{" "}
          Copy
        </Button>
      </Box>

      {!hideBalance && (
        <Box
          sx={{
            bgcolor: "#f9fbfc",
            borderRadius: 3,
            p: 3,
            maxWidth: 520,
          }}
        >
          <Typography variant="h6" gutterBottom>
            <Box
              component="i"
              className="fa-solid fa-dollar-sign"
              sx={{ color: "green.main" }}
            />{" "}
            Current Balance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This balance will be automatically applied towards your next
            invoice.
          </Typography>

          {fetchLoading ? (
            <Skeleton height={60} width="40%" sx={{ my: 1 }} />
          ) : (
            <Box
              sx={{ display: "flex", alignItems: "baseline", gap: 1, my: 2 }}
            >
              <Typography variant="h2">
                ${balance ? formatBalance(balance) : "0.00"}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                USD
              </Typography>
            </Box>
          )}

          {referrals?.length > 0 && (
            <Link
              sx={{ cursor: "pointer", textDecoration: "none", fontSize: 12 }}
              onClick={() => {
                setViewReferrals((prev) => !prev);
                trackEvent("My Referrals Button Clicked");
              }}
            >
              <strong>{viewReferrals ? "Hide" : "My Referrals"}</strong>
            </Link>
          )}

          {viewReferrals && referrals && (
            <Table size="small" sx={{ mt: 1, maxWidth: 460 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      {formatEpochTimestamp({
                        epochTimestamp: row.createdAt,
                        addYear: true,
                      })}
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{formatBalance(row.amount * -1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > referrals?.length && viewReferrals && (
            <Button
              onClick={() =>
                fetchTransactions(referrals?.[referrals?.length - 1]?.id)
              }
              size="small"
              sx={{ fontSize: 12, textTransform: "none", mt: 1 }}
              color="secondary"
            >
              View more
            </Button>
          )}
        </Box>
      )}
    </>
  );
}
